import { useState } from "react";
import {
  Activity,
  CheckCircle2,
  Clock3,
  Download,
  FileText,
  History,
  Lock,
  MessageSquare,
  Paperclip,
  ShieldCheck,
  User2,
} from "lucide-react";
import api from "@/lib/api";
import { formatDateTime } from "@/lib/date";

function safeDate(value) {
  if (!value) return null;

  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

function formatDate(value) {
  if (!value) return "Time unavailable";
  return formatDateTime(value);
}

function normalizeValue(value) {
  if (value === undefined || value === null || value === "") return null;

  if (typeof value === "object") {
    try {
      return JSON.stringify(value);
    } catch {
      return String(value);
    }
  }

  return String(value).replace(/_/g, " ");
}

function eventTitle(event) {
  const type = String(event.event_type || "activity");

  const labels = {
    "ticket.created": "Ticket created",
    "ticket.updated": "Ticket updated",
    "status.changed": "Status changed",
    "resolution.submitted": "Resolution submitted",
    "assignment.changed": "Assignment changed",
    "priority.changed": "Priority changed",
    "severity.changed": "Severity changed",
    "ticket.reopened": "Ticket reopened",
  };

  if (labels[type]) return labels[type];

  if (type.startsWith("status.")) {
    return `Status changed to ${type.replace("status.", "").replace(/_/g, " ")}`;
  }

  return type.replace(/[._]/g, " ");
}

function buildTimeline({
  ticket,
  events = [],
  comments = [],
  approvals = [],
  worklogs = [],
  attachments = [],
}) {
  const items = [];

  if (ticket?.created_at) {
    items.push({
      id: `ticket-created-${ticket.id || ticket._id || "ticket"}`,
      type: "created",
      createdAt: ticket.created_at,
      actorName: ticket.raised_by_name || ticket.created_by_name || "Ticket requester",
      actorRole: "Requester",
      title: "Ticket created",
      body: ticket.description || null,
      raw: ticket,
    });
  }

  events.forEach((event) => {
    const eventType = String(event.event_type || "").toLowerCase();

    // The ticket itself already creates the primary "Ticket created" card.
    // Skip the duplicate backend audit event and its raw metadata payload.
    if (eventType === "ticket.created" || eventType === "created") {
      return;
    }

    items.push({
      id: `event-${event.id}`,
      type: "event",
      createdAt: event.created_at,
      actorName: event.actor_name || "System",
      actorRole: event.actor_role || null,
      title: eventTitle(event),
      body: event.message || null,
      fromValue: normalizeValue(event.from_value),
      toValue: normalizeValue(event.to_value),
      raw: event,
    });
  });

  comments.forEach((comment) => {
    const isInternal = comment.visibility !== "public";

    items.push({
      id: `comment-${comment.id}`,
      type: isInternal ? "internal_comment" : "comment",
      createdAt: comment.created_at,
      actorName: comment.author_name || "User",
      actorRole: comment.author_role || null,
      title: isInternal ? "Internal note" : "Public reply",
      body: comment.body,
      visibility: comment.visibility,
      raw: comment,
    });
  });

  approvals.forEach((approval) => {
    items.push({
      id: `approval-${approval.id}`,
      type: "approval",
      createdAt:
        approval.decided_at ||
        approval.updated_at ||
        approval.created_at,
      actorName:
        approval.decided_by_name ||
        approval.approver_name ||
        approval.requested_by_name ||
        "Approval workflow",
      actorRole: approval.approver_role || null,
      title: `${String(approval.approval_type || "approval").replace(/_/g, " ")}`,
      body:
        approval.comment ||
        approval.reason ||
        null,
      decision: approval.decision || "pending",
      raw: approval,
    });
  });

  worklogs.forEach((worklog) => {
    items.push({
      id: `worklog-${worklog.id}`,
      type: "worklog",
      createdAt: worklog.created_at,
      actorName: worklog.engineer_name || worklog.author_name || "Support engineer",
      actorRole: "Support engineer",
      title: "Work logged",
      body: worklog.activity,
      minutes: worklog.minutes,
      billable: Boolean(worklog.billable),
      raw: worklog,
    });
  });

  attachments.forEach((attachment) => {
    items.push({
      id: `attachment-${attachment.id}`,
      type: "attachment",
      createdAt: attachment.created_at,
      actorName:
        attachment.uploaded_by_name ||
        attachment.author_name ||
        "User",
      actorRole: attachment.uploaded_by_role || null,
      title: "File attached",
      body: attachment.description || null,
      filename:
        attachment.original_name ||
        attachment.filename ||
        "Attachment",
      contentType: attachment.content_type,
      sizeBytes: attachment.size_bytes,
      attachmentId: attachment.id,
      raw: attachment,
    });
  });

  return items
    .filter((item) => safeDate(item.createdAt))
    .sort(
      (a, b) =>
        safeDate(a.createdAt).getTime() -
        safeDate(b.createdAt).getTime()
    );
}

function timelineIcon(type) {
  const common = "h-4 w-4";

  switch (type) {
    case "created":
      return <FileText className={common} />;
    case "comment":
      return <MessageSquare className={common} />;
    case "internal_comment":
      return <Lock className={common} />;
    case "approval":
      return <ShieldCheck className={common} />;
    case "worklog":
      return <Clock3 className={common} />;
    case "attachment":
      return <Paperclip className={common} />;
    default:
      return <Activity className={common} />;
  }
}

function timelineStyle(type) {
  switch (type) {
    case "comment":
      return {
        icon: "bg-blue-500/15 text-blue-300 border-blue-500/30",
        card: "border-blue-500/20 bg-blue-500/[0.04]",
      };
    case "internal_comment":
      return {
        icon: "bg-amber-500/15 text-amber-300 border-amber-500/30",
        card: "border-amber-500/20 bg-amber-500/[0.04]",
      };
    case "approval":
      return {
        icon: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
        card: "border-emerald-500/20 bg-emerald-500/[0.04]",
      };
    case "worklog":
      return {
        icon: "bg-violet-500/15 text-violet-300 border-violet-500/30",
        card: "border-violet-500/20 bg-violet-500/[0.04]",
      };
    case "attachment":
      return {
        icon: "bg-cyan-500/15 text-cyan-300 border-cyan-500/30",
        card: "border-cyan-500/20 bg-cyan-500/[0.04]",
      };
    case "created":
      return {
        icon: "bg-[#d4af37]/15 text-[#e5c158] border-[#d4af37]/30",
        card: "border-[#d4af37]/20 bg-[#d4af37]/[0.04]",
      };
    default:
      return {
        icon: "bg-secondary text-muted-foreground border-border",
        card: "border-border bg-secondary/20",
      };
  }
}

function formatFileSize(bytes) {
  const value = Number(bytes);

  if (!Number.isFinite(value) || value <= 0) return null;
  if (value < 1024) return `${value} B`;
  if (value < 1024 * 1024) return `${(value / 1024).toFixed(1)} KB`;

  return `${(value / (1024 * 1024)).toFixed(1)} MB`;
}

function ApprovalDecision({ decision }) {
  const value = String(decision || "pending").toLowerCase();

  const className =
    value === "approved"
      ? "border-emerald-500/30 bg-emerald-500/10 text-emerald-300"
      : value === "rejected"
        ? "border-rose-500/30 bg-rose-500/10 text-rose-300"
        : "border-amber-500/30 bg-amber-500/10 text-amber-300";

  return (
    <span
      className={`rounded-full border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider ${className}`}
    >
      {value.replace(/_/g, " ")}
    </span>
  );
}

function TimelineCard({ item, isLast }) {
  const styles = timelineStyle(item.type);

  return (
    <li className="relative flex gap-3 pb-5">
      {!isLast && (
        <span className="absolute left-[17px] top-9 h-[calc(100%-20px)] w-px bg-border" />
      )}

      <div
        className={`relative z-10 flex h-9 w-9 shrink-0 items-center justify-center rounded-full border ${styles.icon}`}
      >
        {timelineIcon(item.type)}
      </div>

      <article
        className={`min-w-0 flex-1 rounded-xl border p-4 ${styles.card}`}
        data-testid={item.id}
      >
        <header className="flex flex-wrap items-start justify-between gap-2">
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <h3 className="text-sm font-semibold capitalize">
                {item.title}
              </h3>

              {item.type === "internal_comment" && (
                <span className="rounded bg-amber-500/15 px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-widest text-amber-300">
                  Internal
                </span>
              )}

              {item.type === "approval" && (
                <ApprovalDecision decision={item.decision} />
              )}
            </div>

            <div className="mt-1 flex flex-wrap items-center gap-1.5 text-xs text-muted-foreground">
              <User2 className="h-3 w-3" />
              <span className="font-medium text-foreground">
                {item.actorName}
              </span>

              {item.actorRole && (
                <>
                  <span>·</span>
                  <span className="capitalize">
                    {String(item.actorRole).replace(/_/g, " ")}
                  </span>
                </>
              )}
            </div>
          </div>

          <time className="shrink-0 text-[11px] text-muted-foreground">
            {formatDate(item.createdAt)}
          </time>
        </header>

        {item.body && (
          <div className="mt-3 whitespace-pre-wrap text-sm leading-6 text-foreground/90">
            {item.body}
          </div>
        )}

        {(item.fromValue || item.toValue) && (
          <div className="mt-3 flex flex-wrap items-center gap-2 text-xs">
            {item.fromValue && (
              <span className="rounded-md border border-border bg-background/40 px-2 py-1 text-muted-foreground">
                {item.fromValue}
              </span>
            )}

            {item.fromValue && item.toValue && (
              <span className="text-muted-foreground">→</span>
            )}

            {item.toValue && (
              <span className="rounded-md border border-[#d4af37]/30 bg-[#d4af37]/10 px-2 py-1 text-[#e5c158]">
                {item.toValue}
              </span>
            )}
          </div>
        )}

        {item.type === "worklog" && (
          <div className="mt-3 flex flex-wrap gap-2">
            <span className="rounded-md border border-violet-500/20 bg-violet-500/10 px-2 py-1 text-xs text-violet-200">
              {item.minutes || 0} minutes
            </span>

            <span className="rounded-md border border-border bg-background/30 px-2 py-1 text-xs text-muted-foreground">
              {item.billable ? "Billable" : "Non-billable"}
            </span>
          </div>
        )}

        {item.type === "attachment" && (
          <a
            href={`${api.defaults.baseURL}/attachments/${item.attachmentId}/download`}
            className="mt-3 flex items-center justify-between gap-3 rounded-lg border border-border bg-background/30 p-3 transition hover:border-[#d4af37]/40"
          >
            <div className="flex min-w-0 items-center gap-3">
              <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-secondary">
                <Paperclip className="h-4 w-4 text-[#e5c158]" />
              </div>

              <div className="min-w-0">
                <div className="truncate text-sm font-medium">
                  {item.filename}
                </div>

                <div className="text-xs text-muted-foreground">
                  {[item.contentType, formatFileSize(item.sizeBytes)]
                    .filter(Boolean)
                    .join(" · ") || "Attachment"}
                </div>
              </div>
            </div>

            <Download className="h-4 w-4 shrink-0 text-[#e5c158]" />
          </a>
        )}
      </article>
    </li>
  );
}

export default function ConversationTimeline({
  ticket,
  events = [],
  comments = [],
  approvals = [],
  worklogs = [],
  attachments = [],
}) {
  const [expanded, setExpanded] = useState(false);

  const items = buildTimeline({
    ticket,
    events,
    comments,
    approvals,
    worklogs,
    attachments,
  });

  const DEFAULT_VISIBLE_COUNT = 5;
  const hasMore = items.length > DEFAULT_VISIBLE_COUNT;

  // Timeline is stored chronologically for the connecting line,
  // but the compact view should surface the latest activity.
  const visibleItems = expanded
    ? items
    : items.slice(-DEFAULT_VISIBLE_COUNT);

  if (!items.length) {
    return (
      <div className="rounded-xl border border-dashed border-border p-8 text-center">
        <History className="mx-auto h-7 w-7 text-muted-foreground" />
        <div className="mt-3 text-sm font-medium">No activity yet</div>
        <div className="mt-1 text-xs text-muted-foreground">
          Replies, notes, status changes, approvals, files and work logs will
          appear here.
        </div>
      </div>
    );
  }

  return (
    <div data-testid="conversation-timeline">
      <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="font-display text-lg font-bold">
            Conversation
          </h2>
          <p className="mt-1 text-xs text-muted-foreground">
            Complete communication and activity history for this ticket.
          </p>
        </div>

        <div className="rounded-full border border-border bg-secondary/40 px-3 py-1 text-xs text-muted-foreground">
          {items.length} {items.length === 1 ? "Activity" : "Activities"}
        </div>
      </div>

      {!expanded && hasMore && (
        <div className="mb-4 rounded-lg border border-border bg-secondary/20 px-3 py-2 text-xs text-muted-foreground">
          Showing the latest {DEFAULT_VISIBLE_COUNT} of {items.length} activities.
        </div>
      )}

      <ol>
        {visibleItems.map((item, index) => (
          <TimelineCard
            key={item.id}
            item={item}
            isLast={index === visibleItems.length - 1}
          />
        ))}
      </ol>

      {hasMore && (
        <div className="flex justify-center border-t border-border pt-4">
          <button
            type="button"
            data-testid="conversation-toggle-history"
            onClick={() => setExpanded((current) => !current)}
            className="rounded-lg border border-border bg-secondary/40 px-4 py-2 text-xs font-semibold text-foreground transition hover:border-primary/40 hover:bg-primary/10 hover:text-primary"
          >
            {expanded
              ? "Show Less"
              : `See More (${items.length - DEFAULT_VISIBLE_COUNT} Earlier)`}
          </button>
        </div>
      )}
    </div>
  );
}
